import { useState } from "react";
import { X, ShoppingCart, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useCart, fmtFt } from "@/lib/cart";
import { createOrder } from "@/lib/api";

export default function CartDrawer() {
  const { items, remove, setQty, clear, total, count, open, setOpen } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", shipping_address: "",
    want_mounting: false, is_company: false, company_name: "", vat_number: "", billing_address: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fields = [
    { k: "customer_name", label: "Teljes név *", placeholder: "Teljes név" },
    { k: "phone", label: "Telefon *", placeholder: "+36 30 ...", type: "tel" },
    { k: "email", label: "E-mail *", placeholder: "email@pelda.hu", type: "email" },
    { k: "shipping_address", label: "Szállítási / szerelési cím *", placeholder: "Város, utca, házszám" },
  ];

  const mountPrice = (size) => {
    const m = String(size || "").match(/R\s*(\d+)/i);
    if (!m) return 5500;
    const inch = parseInt(m[1], 10);
    if (inch <= 16) return 5000;
    if (inch <= 18) return 5500;
    if (inch <= 20) return 7000;
    return 9000;
  };
  const mountingFee = form.want_mounting ? Math.max(items.reduce((s, i) => s + mountPrice(i.size) * i.qty, 0), 20000) : 0;
  const grandTotal = total + mountingFee;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const order = await createOrder({
        ...form,
        items: items.map((i) => ({ tire_id: i.tire_id, brand: i.brand, size: i.size, price: i.price, qty: i.qty })),
      });
      setSuccess(order);
      clear();
      toast.success("Rendelés leadva! Hamarosan keresünk.");
    } catch (err) {
      const d = err?.response?.data?.detail || "Hiba történt. Próbáld újra.";
      toast.error(typeof d === "string" ? d : "Ellenőrizd az adatokat.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="cart-drawer"
          className="fixed inset-0 z-[70] flex justify-end"
        >
          <div className="absolute inset-0 bg-gray-100/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="relative w-full max-w-md bg-[#F0F1F5] border-l border-gray-200 flex flex-col"
          >
            <header className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-violet-600" />
                <div>
                  <div className="font-display font-bold text-lg">Kosár</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-400">{count} tétel</div>
                </div>
              </div>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-2 text-gray-500 hover:text-gray-900">
                <X className="w-5 h-5" />
              </button>
            </header>

            {success ? (
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div className="border border-green-500/30 bg-green-500/10 p-6 rounded-sm text-center">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 border border-green-400 grid place-items-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="font-display text-xl font-bold text-gray-900">Rendelés leadva!</div>
                  <div className="mt-2 text-sm text-gray-500">
                    Rendelésszám: <span className="font-mono text-violet-600">#{success.id.slice(0, 8)}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">Hamarosan keresünk a megadott elérhetőségen.</div>
                </div>
                <button
                  onClick={() => { setSuccess(null); setCheckout(false); setOpen(false); }}
                  className="w-full py-3 bg-gray-100/5 border border-gray-300 text-gray-900 font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-gray-100/10"
                >
                  Bezárás
                </button>
              </div>
            ) : items.length === 0 && !checkout ? (
              <div className="flex-1 grid place-items-center p-6">
                <div className="text-center">
                  <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <div className="text-gray-500">A kosár üres</div>
                </div>
              </div>
            ) : !checkout ? (
              <>
                <div className="flex-1 overflow-y-auto">
                  {items.map((it) => (
                    <div key={it.tire_id} data-testid={`cart-item-${it.tire_id}`} className="flex gap-3 p-4 border-b border-gray-200">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">{it.brand}</div>
                        <div className="font-display font-bold text-gray-900 truncate">{it.size}</div>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => setQty(it.tire_id, it.qty - 1)}
                            className="w-7 h-7 grid place-items-center border border-gray-200 text-gray-600 hover:border-violet-400 rounded-sm"
                            data-testid={`cart-dec-${it.tire_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="font-display font-bold w-6 text-center">{it.qty}</span>
                          <button
                            onClick={() => setQty(it.tire_id, it.qty + 1)}
                            className="w-7 h-7 grid place-items-center border border-gray-200 text-gray-600 hover:border-violet-400 rounded-sm"
                            data-testid={`cart-inc-${it.tire_id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => remove(it.tire_id)}
                            className="ml-auto p-1.5 text-red-300 hover:text-red-200"
                            data-testid={`cart-remove-${it.tire_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display font-bold text-gray-900">{fmtFt(it.price * it.qty)}</div>
                        <div className="text-[10px] text-gray-400">{fmtFt(it.price)} / db</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 p-5 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-400">Végösszeg</span>
                    <span className="font-display font-black text-2xl text-gray-900">{fmtFt(grandTotal)}</span>
                  </div>
                  {form.want_mounting && (
                    <div className="text-xs text-gray-400">Szerelés: {fmtFt(mountingFee)} · Gumik: {fmtFt(total)}</div>
                  )}
                  <button
                    onClick={() => setCheckout(true)}
                    data-testid="cart-checkout"
                    className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold text-sm uppercase tracking-wider rounded-sm glow-blue"
                  >
                    Rendelés leadása →
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={onSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  <button type="button" onClick={() => setCheckout(false)} className="text-xs uppercase tracking-wider text-gray-400 hover:text-gray-900">
                    ← Vissza a kosárhoz
                  </button>
                  {fields.map((f) => (
                    <div key={f.k}>
                      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">{f.label}</label>
                      <input
                        data-testid={`checkout-${f.k}`}
                        required
                        type={f.type || "text"}
                        value={form[f.k]}
                        onChange={(e) => update(f.k, e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-violet-400 text-sm"
                      />
                    </div>
                  ))}
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      data-testid="checkout-is-company"
                      type="checkbox"
                      checked={form.is_company}
                      onChange={(e) => update("is_company", e.target.checked)}
                      className="w-4 h-4 accent-violet-500"
                    />
                    <span className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Céges számlázás</span>
                  </label>
                  {form.is_company && (
                    <div className="grid gap-4">
                      {[
                        { k: "company_name", label: "Cégnév *", placeholder: "Cég Kft" },
                        { k: "vat_number", label: "Adószám *", placeholder: "12345678-1-42" },
                        { k: "billing_address", label: "Számlázási cím *", placeholder: "Város, utca, házszám" },
                      ].map((f) => (
                        <div key={f.k}>
                          <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">{f.label}</label>
                          <input
                            data-testid={`checkout-${f.k}`}
                            required
                            value={form[f.k]}
                            onChange={(e) => update(f.k, e.target.value)}
                            placeholder={f.placeholder}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-violet-400 text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      data-testid="checkout-want-mounting"
                      type="checkbox"
                      checked={form.want_mounting}
                      onChange={(e) => update("want_mounting", e.target.checked)}
                      className="w-4 h-4 accent-violet-500"
                    />
                    <span className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Helyszíni szereléssel (Budapest)</span>
                  </label>
                  {form.want_mounting && (
                    <div className="border border-violet-500/30 bg-violet-500/5 rounded-sm p-3 space-y-1 text-xs">
                      {items.map((it) => (
                        <div key={it.tire_id} className="flex justify-between text-gray-500">
                          <span>{it.brand} {it.size} × {it.qty}</span>
                          <span>{fmtFt(mountPrice(it.size))} / db</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-gray-900 font-bold pt-1 border-t border-violet-500/20">
                        <span>Szerelés összesen (min. {fmtFt(20000)})</span>
                        <span>{fmtFt(mountingFee)}</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">Megjegyzés</label>
                    <textarea
                      data-testid="checkout-note"
                      rows={3}
                      value={form.note}
                      onChange={(e) => update("note", e.target.value)}
                      placeholder="Opcionális — pl. kívánt időpont, autó típusa"
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-violet-400 text-sm resize-none"
                    />
                  </div>
                </div>
                <div className="border-t border-gray-200 p-5 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-400">Fizetendő</span>
                    <span className="font-display font-black text-xl text-gray-900">{fmtFt(total)}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 leading-relaxed">
                    Fizetés személyesen vagy átvételkor. Rendelés leadása után telefonon egyeztetünk az átvételről / szerelésről.
                  </div>
                  <button
                    data-testid="checkout-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold text-sm uppercase tracking-wider rounded-sm glow-blue disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Rendelés leadása
                  </button>
                </div>
              </form>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
